import React, { useState } from "react";
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { extractErrorMessage } from "../../utils/errorMessage";
import { getSavedEmail, setSavedEmail } from "../../utils/authPersist";

export default function LoginNew() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { search } = useLocation();
  const redirect = new URLSearchParams(search).get("redirect") || "/EmployeeDashboard";
  const err = new URLSearchParams(search).get("error");

  const [email, setEmail] = useState(() => getSavedEmail());
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear, visible focus/selection state for the inputs — a bolder primary
  // border plus a soft ring so it's obvious which field is active.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      transition: "box-shadow 0.15s ease, border-color 0.15s ease",
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(108,80,191,0.20)" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "primary.main",
        borderWidth: 2,
      },
    },
    "& label.Mui-focused": { color: "primary.main" },
    // Highlight selected/dragged text inside the field too.
    "& input::selection": { backgroundColor: "rgba(108,80,191,0.30)" },
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter email and password");
    setLoading(true);
    try {
      await login(email, password, { remember });
      if (remember) setSavedEmail(email);
      else setSavedEmail("");
      toast.success("Welcome back!");
      nav(redirect, { replace: true });
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex" }}>
      {/* Left pane - hero */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          px: 6, py: 8,
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #6C50BF 0%, #492ba3 60%, #4178E5 100%)",
          color: "white",
        }}
      >
        <Box sx={{ position: "absolute", top: -200, right: -100, width: 500, height: 500, background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -150, left: -150, width: 400, height: 400, background: "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" }} />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={6}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18 }}>
              IU
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1 }}>InfyU</Typography>
              <Typography sx={{ fontSize: 11, letterSpacing: "0.1em", opacity: 0.85, fontWeight: 600 }}>HRMS PORTAL</Typography>
            </Box>
          </Stack>
          <Typography sx={{ fontSize: "2.5rem", fontWeight: 800, lineHeight: 1.15, mb: 2, maxWidth: 480 }}>
            Welcome back to your workspace.
          </Typography>
          <Typography sx={{ fontSize: 16, opacity: 0.9, mb: 4, maxWidth: 460 }}>
            Sign in to access attendance, leaves, payroll, goals, and everything else you need for your workday.
          </Typography>
          <Stack spacing={1.5}>
            {["Modern dashboard with real-time insights", "Seamless leave, attendance and payroll", "Built for Indian compliance — PF, ESI, TDS, Form 16"].map((t, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontSize: 14, opacity: 0.95 }}>{t}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Right pane - form */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, md: 4 }, py: 4 }}>
        <Box sx={{ width: "100%", maxWidth: 440 }}>
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", mb: 4 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, background: "linear-gradient(135deg, #6C50BF 0%, #492ba3 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, mr: 1.5 }}>IU</Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>InfyU HRMS</Typography>
          </Box>

          <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, mb: 1 }}>Sign in</Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 4 }}>
            Enter your credentials to access your workspace
          </Typography>

          {err === "sso_user_not_found" && <Typography sx={{ fontSize: 13, color: "error.main", mb: 2 }}>Your SSO account isn't linked to any employee. Contact HR.</Typography>}
          {err === "account_archived" && <Typography sx={{ fontSize: 13, color: "error.main", mb: 2 }}>Your account is archived. Contact HR.</Typography>}
          {err === "sso_failed" && <Typography sx={{ fontSize: 13, color: "error.main", mb: 2 }}>SSO login failed. Please try again.</Typography>}

          {/* method=post + name attributes help browsers offer Save password / autofill */}
          <Box component="form" method="post" action="/login" autoComplete="on" onSubmit={submit}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                size="medium"
                id="login-email"
                name="email"
                type="email"
                label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
                autoFocus={!email}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                size="medium"
                id="login-password"
                name="password"
                type={show ? "text" : "password"}
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShow((s) => !s)} tabIndex={-1} type="button">
                        {show ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <FormControlLabel
                  control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} size="small" />}
                  label={<Typography sx={{ fontSize: 13 }}>Remember me</Typography>}
                />
                <Button component={Link} to="/forgot-password" size="small" sx={{ fontSize: 13, fontWeight: 600 }}>
                  Forgot password?
                </Button>
              </Stack>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                endIcon={loading ? null : <LoginOutlinedIcon />}
                sx={{ py: 1.5, fontSize: 15 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "Sign in"}
              </Button>

              <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "center", mt: 2 }}>
                Keep &quot;Remember me&quot; on to stay signed in after closing the browser. Your browser may also offer to save email and password.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
